// frontend/src/components/SelfAssessmentTab.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./SelfAssessmentTab.css";

const ROLE_OPTIONS = [
  "Keluarga Petani",
  "Penggerak Pemuda",
  "Agripreneur",
  "Fasilitator Lapangan",
  "Supervisor",
];

const TIMELINES = [
  "3 Tahun Pertama",
  "3 Tahun Kedua",
  "3 Tahun Ketiga",
  "Opsional",
];

const FUNCTIONAL_GRADE_OPTIONS = ["Belum Paham", "Esensial", "Advance", "Expert"];
const LEADERSHIP_GRADE_OPTIONS = ["Tidak Terampil", "Terampil", "Berlebihan"];

const FUNCTIONAL_GRADE_RANK = {
  "Belum Paham": 0,
  Esensial: 1,
  Advance: 2,
  Expert: 3,
};

const LEADERSHIP_GRADE_RANK = {
  "Tidak Terampil": 1,
  Terampil: 2,
  Berlebihan: 3,
};

const normalizeLeadershipGrade = (grade) => {
  const g = String(grade || "").trim();
  if (!g) return "";
  if (g === "Esensial") return "Tidak Terampil";
  if (g === "Advance") return "Terampil";
  if (g === "Expert") return "Berlebihan";
  return g;
};

const getGradeOptionsByType = (type) =>
  type === "Kepemimpinan" ? LEADERSHIP_GRADE_OPTIONS : FUNCTIONAL_GRADE_OPTIONS;

const getRankByType = (grade, type) => {
  const g = String(grade || "");
  if (type === "Kepemimpinan") {
    return LEADERSHIP_GRADE_RANK[normalizeLeadershipGrade(g)] ?? -1;
  }
  return FUNCTIONAL_GRADE_RANK[g] ?? -1;
};

const getRequiredByType = (standard, type) => {
  const raw = String(standard || "").trim();
  if (type === "Kepemimpinan") {
    return normalizeLeadershipGrade(raw || "Esensial") || "Tidak Terampil";
  }
  return raw || "Esensial";
};

const normalizeSelectedByType = (grade, type) => {
  if (type === "Kepemimpinan") return normalizeLeadershipGrade(grade);
  return String(grade || "");
};

const getGuestSessionId = () => {
  const key = "sa:guestSessionId";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;

    const generated =
      window.crypto?.randomUUID?.() ||
      `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, generated);
    return generated;
  } catch {
    return `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

export default function SelfAssessmentTab() {
  const { user } = useContext(AuthContext);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  const leftPaneRef = useRef(null);
  const pendingScrollRef = useRef(null);

  const [displayName, setDisplayName] = useState("");
  const [displayInstitution, setDisplayInstitution] = useState("");
  const [displayAddress, setDisplayAddress] = useState("");

  const [role, setRole] = useState(ROLE_OPTIONS[0]);
  const [timeline, setTimeline] = useState(TIMELINES[0]);
  const [typeFilter, setTypeFilter] = useState("");

  const [allMap, setAllMap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);

  const [selected, setSelected] = useState({}); // { [compId]: grade }
  const [notes] = useState({}); // opsional
  const needsInstitution = role === "Fasilitator Lapangan" || role === "Supervisor";

  /* ---------- Helpers localStorage ---------- */
  const storageKey = (uid, r, tl) => `sa:${uid || "guest"}:${r}:${tl}`;

  const loadFromLocal = (uid, r, tl) => {
    try {
      const raw = localStorage.getItem(storageKey(uid, r, tl));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.selected || null;
    } catch {
      return null;
    }
  };

  const saveToLocal = (uid, r, tl, map) => {
    try {
      localStorage.setItem(
        storageKey(uid, r, tl),
        JSON.stringify({ selected: map, ts: Date.now() })
      );
    } catch (e) {
      console.warn("localStorage error:", e);
    }
  };

  /* ---------- Load competencies & saved grades ---------- */
  const loadMap = async (r) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${apiUrl}/capdev/competencies/by-role-timeline`,
        { params: { role: r } }
      );
      setAllMap(data?.data || []);
    } catch (e) {
      console.error("loadMap error:", e);
      setAllMap([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSaved = async (uId, r, tl) => {
    const guestSessionId = getGuestSessionId();
    const localKey = uId || guestSessionId || "guest";
    const local = loadFromLocal(localKey, r, tl);
    if (local && Object.keys(local).length) setSelected(local);

    try {
      const { data } = await axios.get(`${apiUrl}/capdev/self-assessments`, {
        params: {
          ...(uId ? { user_id: uId } : { guest_session_id: guestSessionId }),
          role: r,
          timeline: tl,
        },
      });
      if (data?.success) {
        const m = {};
        (data.data || []).forEach((row) => {
          m[row.competency_id] = row.self_grade;
        });
        if (Object.keys(m).length) {
          setSelected(m);
          saveToLocal(localKey, r, tl, m);
        }
      }
    } catch (e) {
      console.error("loadSaved error:", e);
    }
  };

  useEffect(() => {
    loadMap(role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    loadSaved(user?.id || null, role, timeline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, role, timeline]);

  useEffect(() => {
    setRecommendations([]);
    setHasSavedOnce(false);
  }, [role, timeline, typeFilter]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("sa:profile");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setDisplayName(String(parsed?.name || ""));
      setDisplayInstitution(String(parsed?.institution || ""));
      setDisplayAddress(String(parsed?.address || ""));
    } catch (e) {
      console.warn("load local profile for self assessment failed:", e);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        "sa:profile",
        JSON.stringify({
          name: displayName,
          institution: displayInstitution,
          address: displayAddress,
        })
      );
    } catch (e) {
      console.warn("save local profile for self assessment failed:", e);
    }
  }, [displayName, displayInstitution, displayAddress]);

  const compsByTimeline = useMemo(() => {
    const list = (allMap || []).filter((x) => x.timeline === timeline);
    const fungsional = list.filter((x) => x.type === "Fungsional");
    const kepemimpinan = list.filter((x) => x.type === "Kepemimpinan");
    return { fungsional, kepemimpinan };
  }, [allMap, timeline]);

  const filteredComps = useMemo(() => {
    if (!typeFilter) return compsByTimeline;
    if (typeFilter === "Fungsional") {
      return { fungsional: compsByTimeline.fungsional, kepemimpinan: [] };
    }
    return { fungsional: [], kepemimpinan: compsByTimeline.kepemimpinan };
  }, [compsByTimeline, typeFilter]);

  const totalSelected = useMemo(() => {
    const visible = filteredComps.fungsional.concat(filteredComps.kepemimpinan);
    const visibleIds = new Set(visible.map((v) => String(v.id)));
    return Object.entries(selected).filter(
      ([cid, grade]) => grade && visibleIds.has(String(cid))
    ).length;
  }, [selected, filteredComps]);

  const restoreScrollPosition = () => {
    const snapshot = pendingScrollRef.current;
    if (!snapshot) return;
    pendingScrollRef.current = null;

    requestAnimationFrame(() => {
      if (leftPaneRef.current) {
        leftPaneRef.current.scrollTop = snapshot.leftPaneTop;
      }
      window.scrollTo(snapshot.windowX, snapshot.windowY);
    });
  };

  const setGrade = (compId, grade) => {
    pendingScrollRef.current = {
      leftPaneTop: leftPaneRef.current?.scrollTop || 0,
      windowX: window.scrollX,
      windowY: window.scrollY,
    };
    setSelected((prev) => ({ ...prev, [compId]: grade }));
  };

  useEffect(() => {
    restoreScrollPosition();
  }, [selected]);

  const shortCompName = (name) => {
    const raw = String(name || "").trim();
    if (!raw) return "";
    const first = raw.split(" - ")[0]?.trim();
    return first || raw;
  };  const buildRecommendations = () => {
    const visible = filteredComps.fungsional.concat(filteredComps.kepemimpinan);
    const recs = [];

    for (const comp of visible) {
      const required = getRequiredByType(comp.standard, comp.type);
      const current = normalizeSelectedByType(selected[comp.id], comp.type);
      const reqRank = getRankByType(required, comp.type);
      const curRank = getRankByType(current, comp.type);

      if (curRank < reqRank) {
        recs.push({
          competencyId: comp.id,
          competency: shortCompName(comp.name),
          required,
          current: current || "Belum diisi",
          text: `Peningkatan pemahaman ${shortCompName(comp.name)}`,
        });
      }
    }
    return recs;
  };

  const save = async () => {
    if (savingAssessment) return;
    setSavingAssessment(true);
    try {
      const visibleCompIds = filteredComps.fungsional
        .concat(filteredComps.kepemimpinan)
        .map((c) => c.id);      const visibleById = new Map(
        filteredComps.fungsional
          .concat(filteredComps.kepemimpinan)
          .map((c) => [String(c.id), c])
      );

      const items = Object.entries(selected)
        .filter(([cid]) => visibleCompIds.includes(Number(cid)))
        .map(([cid, grade]) => {
          const comp = visibleById.get(String(cid));
          return {
            competency_id: Number(cid),
            self_grade: normalizeSelectedByType(grade, comp?.type),
            notes: notes[cid] || null,
          };
        });

      if (items.length === 0) {
        alert("Tidak ada penilaian yang dipilih.");
        return;
      }

      const guestSessionId = getGuestSessionId();
      const localKey = user?.id || guestSessionId || "guest";
      saveToLocal(localKey, role, timeline, selected);

      const payload = {
        ...(user?.id ? { user_id: user.id } : { guest_session_id: guestSessionId }),
        respondent_name: displayName,
        respondent_institution: needsInstitution ? displayInstitution : "",
        respondent_address: displayAddress,
        role,
        timeline,
        items,
      };
      const { data } = await axios.post(
        `${apiUrl}/capdev/self-assessments`,
        payload
      );
      if (!data?.success) {
        alert(data?.message || "Gagal menyimpan ke server, tetapi tersimpan lokal.");
        return;
      }

      const recs = buildRecommendations();
      setRecommendations(recs);
      setHasSavedOnce(true);
      alert("Penilaian tersimpan.");
      await loadSaved(user?.id || null, role, timeline);
    } catch (e) {
      console.error("Save assessments error:", e);
      alert("Gagal menyimpan penilaian (lokal & server).");
    } finally {
      setSavingAssessment(false);
    }
  };  const getIndicator = (comp, grade) => {
    const g = normalizeSelectedByType(grade, comp.type);

    if (comp.type === "Kepemimpinan") {
      switch (g) {
        case "Tidak Terampil":
          return comp.essential || "-";
        case "Terampil":
          return comp.advance || "-";
        case "Berlebihan":
          return comp.expert || "-";
        default:
          return "Belum mengisi penilaian kepemimpinan.";
      }
    }

    switch (g) {
      case "Esensial":
        return comp.essential || "-";
      case "Advance":
        return comp.advance || "-";
      case "Expert":
        return comp.expert || "-";
      case "Belum Paham":
      default:
        return "Belum memahami indikator esensial (butuh pendampingan dasar).";
    }
  };  const Row = ({ comp, gradeOptions }) => {
    const currentGrade = normalizeSelectedByType(selected[comp.id], comp.type);
    return (
      <>
        <div className="sa-td sa-col-name">
          <div className="sa-name">{comp.name}</div>
        </div>

        {gradeOptions.map((g) => (
          <label key={g} className="sa-td sa-col-grade sa-radio">
            <input
              type="radio"
              name={`comp-${comp.id}`}
              checked={currentGrade === g}
              onChange={() => setGrade(comp.id, g)}
            />
            <div className="sa-indicator">
              <div className="sa-grade">{g}</div>
              <div className="sa-desc">{getIndicator(comp, g)}</div>
            </div>
          </label>
        ))}
      </>
    );
  };  const Section = ({ title, items, type }) => {
    if (!items || items.length === 0) return null;
    const gradeOptions = getGradeOptionsByType(type);

    return (
      <div className="sa-section">
        <h4 className="sa-section-title">{title}</h4>
        <div className="sa-table-wrap">
          <div
            className="sa-table"
            style={{
              gridTemplateColumns: `minmax(140px, 0.85fr) repeat(${gradeOptions.length}, minmax(0, 1.04fr))`,
            }}
          >
            <div className="sa-th sa-col-name">Kompetensi</div>
            {gradeOptions.map((g) => (
              <div key={g} className="sa-th sa-col-grade">
                {g}
              </div>
            ))}

            {items.map((it) => (
              <Row key={it.id} comp={it} gradeOptions={gradeOptions} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sa-wrap">
      <div className="sa-toolbar">
        <div className={`sa-filters ${needsInstitution ? "has-institution" : ""}`}>
          <label className="sa-field sa-field-wide">
            <span>Nama</span>
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Masukkan nama" />
          </label>

          {needsInstitution && (
            <label className="sa-field sa-field-wide">
              <span>Institusi</span>
              <input
                type="text"
                value={displayInstitution}
                onChange={(e) => setDisplayInstitution(e.target.value)}
                placeholder="Masukkan institusi"
              />
            </label>
          )}

          <label className="sa-field sa-field-wide">
            <span>Alamat</span>
            <input type="text" value={displayAddress} onChange={(e) => setDisplayAddress(e.target.value)} placeholder="Masukkan alamat" />
          </label>

          <label className="sa-field">
            <span>Role Anda di Komunitas</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>

          <label className="sa-field">
            <span>Tahapan</span>
            <select value={timeline} onChange={(e) => setTimeline(e.target.value)}>
              {TIMELINES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className="sa-field">
            <span>Jenis Kompetensi</span>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Semua</option>
              <option value="Fungsional">Fungsional</option>
              <option value="Kepemimpinan">Kepemimpinan</option>
            </select>
          </label>
        </div>

        <button
          className={`sa-save ${totalSelected === 0 ? "disabled" : ""} ${savingAssessment ? "loading" : ""}`}
          disabled={totalSelected === 0 || savingAssessment}
          onClick={save}
        >
          {savingAssessment && <span className="sa-spinner" aria-hidden="true" />}
          <span>{savingAssessment ? "Memproses..." : "Lihat Rekomendasi"}</span>
        </button>
      </div>

      {loading ? (
        <div className="sa-muted">Memuat...</div>
      ) : filteredComps.fungsional.length === 0 &&
        filteredComps.kepemimpinan.length === 0 ? (
        <div className="sa-muted">Belum ada kompetensi untuk filter tersebut.</div>
      ) : (
        <div className="sa-main-layout">
          <div className="sa-left-pane" ref={leftPaneRef}>
            <Section title="Fungsional" items={filteredComps.fungsional} type="Fungsional" />
            <Section title="Kepemimpinan" items={filteredComps.kepemimpinan} type="Kepemimpinan" />
          </div>

          <aside className="sa-right-pane">
            <div className="sa-rec-card">
              <h4 className="sa-rec-title">Rekomendasi Peningkatan Kompetensi</h4>
              {savingAssessment ? (
                <div className="sa-rec-empty">Menyimpan penilaian dan menyiapkan rekomendasi...</div>
              ) : !hasSavedOnce ? (
                <div className="sa-rec-empty">Rekomendasi muncul setelah klik Lihat Rekomendasi.</div>
              ) : recommendations.length === 0 ? (
                <div className="sa-rec-empty">Sudah sesuai standar pathway untuk filter ini.</div>
              ) : (
                <ul className="sa-rec-list">
                  {recommendations.map((r) => (
                    <li key={r.competencyId} className="sa-rec-item">
                      <span className="sa-rec-main">{r.text}</span>
                      <span className="sa-rec-meta">
                        Anda perlu ada di level: {r.required}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}













